// Geteilter Builder für Share-Nachrichten — Voter-Einladung + Result-Card.
// WhatsApp-Markdown (*fett* / _kursiv_) für die WhatsApp-Story; in anderen
// Apps zeigen sich die Marker zwar als Text, das Layout sieht trotzdem
// aufgeräumt aus.

import type { Appointment, FinderConfig } from './types';
import { formatDateShort, formatTime } from './time';

export type VoterShareInput = {
  title: string | null;
  config: FinderConfig;
  slots: Appointment[];
  voterUrl: string;
  location?: string | null;
};

export type ShareMessage = {
  title: string;
  text: string;
  url: string;
  /** mailto:-Subject + Body, fertig zusammengesetzt. */
  mailto: string;
  /** WhatsApp-URL (wa.me) mit URL-codiertem Text. Geht auch in WhatsApp-Web. */
  whatsapp: string;
};

/** Einladungs-Nachricht für Voter ("Stimm hier ab"). */
export function buildVoterShareMessage(input: VoterShareInput): ShareMessage {
  const titleStr = input.title?.trim() || 'Termin-Abstimmung';
  const dateFrom = formatDateShort(input.config.rangeStart);
  const dateTo   = formatDateShort(input.config.rangeEnd);
  const dayFrom  = formatTime(input.config.dayStartMinutes);
  const dayTo    = formatTime(input.config.dayEndMinutes);
  const slots    = input.slots.length;
  const slotWord = slots === 1 ? 'Termin' : 'Termine';
  const dateLine = input.config.rangeStart === input.config.rangeEnd
    ? `📅 ${dateFrom}`
    : `📅 ${dateFrom} – ${dateTo}`;

  const lines = [
    `🗓️  *${titleStr}*`,
    '',
    dateLine,
    `⏰ ${dayFrom}–${dayTo} Uhr`,
    `🎯 ${slots} ${slotWord} zur Auswahl`,
  ];
  if (input.location?.trim()) {
    lines.push(`📍 ${input.location.trim()}`);
  }
  lines.push(
    '',
    '👉 Bitte stimm hier ab, an welchen Terminen du _kannst_, _vielleicht_ oder _nicht_:',
    '',
    '✨ _Privacy-First — keine Konten, keine Tracker, kein Tracking._',
  );
  const text = lines.join('\n');

  const sharedTitle = input.title ? `${titleStr} — Slot` : 'Slot — Termin abstimmen';
  const fullBody = `${text}\n\n${input.voterUrl}`;
  return {
    title: sharedTitle,
    text,
    url: input.voterUrl,
    mailto: `mailto:?subject=${encodeURIComponent(sharedTitle)}&body=${encodeURIComponent(fullBody)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(fullBody)}`,
  };
}

export type ResultShareInput = {
  title: string | null;
  finalSlots: Appointment[];
  finalMessage: string | null;
  voterUrl: string;
  location?: string | null;
};

/** Ergebnis-Nachricht ("Termin steht: …"). */
export function buildResultShareMessage(input: ResultShareInput): ShareMessage {
  const titleStr = input.title?.trim() || 'Termin-Abstimmung';
  const slotLines = input.finalSlots.map((s) => (
    `📅 ${formatDateShort(s.date)} · ${formatTime(s.startMinutes)}–${formatTime(s.endMinutes)}`
  )).join('\n');
  const lines: string[] = [
    `✅  *Termin steht: ${titleStr}*`,
    '',
    slotLines,
  ];
  if (input.location?.trim()) {
    lines.push(`📍 ${input.location.trim()}`);
  }
  if (input.finalMessage) {
    lines.push('', `_${input.finalMessage}_`);
  }
  lines.push('', '_— über Slot, der Privacy-First Terminfinder._');
  const text = lines.join('\n');
  const sharedTitle = `${titleStr} — Termin steht`;
  const fullBody = `${text}\n\n${input.voterUrl}`;
  return {
    title: sharedTitle,
    text,
    url: input.voterUrl,
    mailto: `mailto:?subject=${encodeURIComponent(sharedTitle)}&body=${encodeURIComponent(fullBody)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(fullBody)}`,
  };
}
